import { OG_IMAGE, SITE_NAME, SITE_URL, seoByPath } from '../data/seo'
import { founder } from '../data/content'
import { policyCopy } from '../data/policies'
import { euro } from '../core/format/currency'
import { SEO_DESCRIPTION_MAX, type SeoResolved } from '../core/rules/seo'
import type { Product, Routine, Treatment } from '../core/types/catalog'
import type { Article } from '../core/types/content'
import type { SeoPageKey } from '../core/types/pages'

/**
 * De automatische Google-teksten: wat de site toont als Sarah niets eigens invult. Gedeeld door
 * de webshop (de echte <title> en meta) en het beheer (de grijze voorbeeldtekst in het veld).
 */

/** Knip nooit vóór dit teken af, ook niet op een woordgrens: anders blijft er te weinig over. */
const MIN_CUT = 80

/** Knip op een woordgrens, zodat er nooit een half woord staat. */
export function clampDescription(text: string, max = SEO_DESCRIPTION_MAX) {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max - 1)
  const space = cut.lastIndexOf(' ')
  return `${cut.slice(0, space > MIN_CUT ? space : cut.length).replace(/[,.;:\s]+$/, '')}…`
}

/** Relatieve of kapotte afbeeldingspaden worden een volledige URL (of de algemene deelafbeelding). */
export function absoluteUrl(src: string) {
  try {
    return new URL(src, SITE_URL).href
  } catch {
    return OG_IMAGE
  }
}

/** Meta-beschrijving: eigen productzin, of — als die leeg is — het "waarom", altijd met prijs en belofte. */
export function productDescription(product: Product) {
  const lead = (product.note || product.why || product.name).trim().replace(/\.?$/, '.')
  const facts = [product.volume, euro(product.price)].filter(Boolean).join(', ')
  return clampDescription(`${lead} ${facts}. Gratis advies van ${founder.firstName}, ${policyCopy.returnsShort}.`)
}

export const productSeoAuto = (p: Product): SeoResolved => ({
  title: `${p.brand} ${p.name} kopen | ${SITE_NAME}`,
  description: productDescription(p),
  image: p.image ? absoluteUrl(p.image) : OG_IMAGE,
})

export const articleSeoAuto = (a: Article): SeoResolved => ({
  title: `${a.title} | ${SITE_NAME}`,
  description: clampDescription(a.excerpt),
  image: a.image ? absoluteUrl(a.image) : OG_IMAGE,
})

export const routineSeoAuto = (r: Routine): SeoResolved => ({
  title: `${r.name}: thuisprotocol | ${SITE_NAME}`,
  description: clampDescription([r.claim, r.forWho].filter(Boolean).join(' ')),
  image: r.image ? absoluteUrl(r.image) : OG_IMAGE,
})

export const treatmentSeoAuto = (t: Treatment): SeoResolved => ({
  title: `${t.name} in Amsterdam | ${SITE_NAME}`,
  description: clampDescription([t.tagline, t.description].filter(Boolean).join('. ')),
  image: OG_IMAGE,
})

/** Het adres van elke vaste pagina met een eigen Google-regel. */
export const seoPagePaths: Record<SeoPageKey, string> = {
  home: '/',
  routines: '/routines',
  treatments: '/behandelingen',
  journal: '/journal',
  about: '/over-ons',
  contact: '/contact',
}

export const isSeoPageKey = (key: string): key is SeoPageKey => key in seoPagePaths

/** De standaardtekst van een vaste pagina (data/seo.ts). */
export function pageSeoAuto(key: SeoPageKey): SeoResolved {
  const entry = seoByPath[seoPagePaths[key]]
  return { title: entry?.title ?? SITE_NAME, description: entry?.description ?? '', image: OG_IMAGE }
}
